import React from "react";
import {FiArrowRight, FiChevronDown, FiExternalLink} from "react-icons/fi";
import {Flight} from "../../models/Flight";
import {RoundTripFlight} from "../../models/RoundTripFlight";
import {useTranslation} from "../../i18n/LanguageProvider";
import {buildOneWayBookingLink} from "../list/RoundTripFlightInfoBlock";
import "./shortlist.css";

const Arrow = FiArrowRight as unknown as React.FC<{size?: number}>;
const Chevron = FiChevronDown as unknown as React.FC<{size?: number}>;
const External = FiExternalLink as unknown as React.FC<{size?: number}>;

const day = (date: string) => Date.parse(`${date}T00:00:00Z`);

function Journey({legs, label}: {legs: Flight[]; label: string}) {
  const {locale, t} = useTranslation();
  const first = legs[0];
  const last = legs[legs.length - 1];
  const date = new Date(`${first.departure_date}T12:00:00Z`);
  const arrivalDay = day(last.arrival_date || last.departure_date)
    + (!last.arrival_date && last.arrival_time < last.departure_time ? 86400000 : 0);
  const extraDays = Math.max(0, Math.round((arrivalDay - day(first.departure_date)) / 86400000));
  const endpoint = (time: string, iata: string, airport: string, arrival = false) => <div className={`min-w-0 ${arrival ? "text-right" : ""}`}>
    <p className={`flex items-baseline gap-1.5 whitespace-nowrap ${arrival ? "justify-end" : ""}`}>
      <span className="text-sm font-bold tabular-nums text-[#30263F]">{time.slice(0, 5)}
        {arrival && extraDays > 0 && <sup className="ml-0.5 text-[9px] text-[#8B72B0]">+{extraDays}</sup>}
      </span>
      <span className="text-[10px] font-semibold text-[#8B72B0]">{iata}</span>
    </p>
    <p title={airport} className="mt-0.5 truncate text-[11px] text-[#878092]">{airport}</p>
  </div>;

  return <div className="shortlist-journey">
    <div className="min-w-0 border-r border-[#E9E2F2] pr-2">
      <p className="text-[9px] font-bold uppercase tracking-wider text-[#A18CBF]">{label}</p>
      <time dateTime={first.departure_date} title={date.toLocaleDateString(locale, {dateStyle: "full", timeZone: "UTC"})}
        className="mt-0.5 block whitespace-nowrap text-[11px] font-semibold text-[#62596F]">{date.toLocaleDateString(locale, {day: "2-digit", month: "short", timeZone: "UTC"})}</time>
    </div>
    <div className="min-w-0">
      <div className="grid grid-cols-[minmax(0,1fr)_16px_minmax(0,1fr)] items-center gap-2">
        {endpoint(first.departure_time, first.departure_iata, first.departure_airport)}
        <span aria-hidden="true" className="text-[#B7A0D8]"><Arrow size={14}/></span>
        {endpoint(last.arrival_time, last.destination_iata, last.destination_airport, true)}
      </div>
      {legs.length > 1 && <p className="mt-1 truncate text-[10px] text-[#8B72B0]"
        title={legs.slice(0, -1).map(leg => leg.destination_airport).join(" · ")}>
        {t("connection.via", {airport: legs.slice(0, -1).map(leg => leg.destination_iata).join(" · ")})} · {t("connection.selfTransfer")}
      </p>}
    </div>
  </div>;
}

export default function ShortlistFlight({flight, action}: {flight: RoundTripFlight; action?: React.ReactNode}) {
  const {locale, t} = useTranslation();
  const outbound = flight.outbound_flights?.length ? flight.outbound_flights : [flight.outbound_flight];
  const inbound = flight.inbound_flights?.length ? flight.inbound_flights : flight.inbound_flight ? [flight.inbound_flight] : [];
  const hasConnection = outbound.length > 1 || inbound.length > 1;
  const nights = inbound.length ? Math.max(0, Math.round((day(inbound[0].departure_date) - day(outbound[0].departure_date)) / 86400000)) : null;
  const estimated = [...outbound, ...inbound].some(leg => leg.price_is_estimated);
  const destination = flight.destination_city || outbound[outbound.length - 1].destination_airport;
  const route = `${flight.origin_city || outbound[0].departure_airport} → ${destination}`;
  const formatDate = (date: string) => new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, {day: "2-digit", month: "short", timeZone: "UTC"});
  const dateRange = `${formatDate(outbound[0].departure_date)}${inbound.length ? ` – ${formatDate(inbound[0].departure_date)}` : ""}`;

  return <div className="relative">
    {action && <div className="absolute right-0 top-0 z-10">{action}</div>}
    <details className="shortlist-card">
      <summary className="cursor-pointer list-none rounded-lg text-left [&::-webkit-details-marker]:hidden">
        <div className={`flex min-h-[32px] items-center ${action ? "pr-9" : ""}`}>
          <h2 title={route} className="min-w-0 flex-1 truncate text-sm font-bold leading-snug text-[#30263F]">{route}</h2>
        </div>
        <div className="mt-1 flex items-center gap-3">
          <p className="min-w-0 flex-1 truncate text-[11px] text-[#93859F]">
            <span className="font-semibold text-[#62596F]">{dateRange}</span>
            <span className="mx-1.5 text-[#D3C7E0]">·</span>
            {nights === null ? t("flight.oneWay") : t(nights === 1 ? "shortlist.night" : "shortlist.nights", {count: nights})}
            <span className="mx-1.5 text-[#D3C7E0]">·</span>{t(hasConnection ? "connection.oneStop" : "connection.direct")}
          </p>
          <p title={t("share.totalPrice")} className="shrink-0 text-right text-lg font-bold tabular-nums tracking-tight text-[#712FFF]">
            {estimated && <span className="mr-1 text-[10px] font-medium">{t("flight.fromPrice")}</span>}
            {new Intl.NumberFormat(locale, {style: "currency", currency: "EUR", maximumFractionDigits: 2, minimumFractionDigits: Number.isInteger(flight.total_price) ? 0 : 2}).format(flight.total_price)}
          </p>
          <span aria-hidden="true" className="flex h-7 w-5 shrink-0 items-center justify-center text-[#A18CBF]">
            <span className="shortlist-card-chevron transition-transform"><Chevron size={16}/></span>
          </span>
        </div>
      </summary>
      <div className="mt-3 border-t border-[#EDE6F5] pt-3">
        <div className="space-y-2 rounded-xl bg-[#F8F6FC] px-3 py-1.5">
          <Journey legs={outbound} label={t("flight.outbound")}/>
          {inbound.length > 0 && <Journey legs={inbound} label={t("flight.inbound")}/>}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
          {hasConnection ? <>
            <span className="mr-auto text-[10px] text-[#93859F]">{t("flight.bookLegs", {count: outbound.length + inbound.length})}</span>
            {[...outbound, ...inbound].map((leg, index) => <a key={index} href={buildOneWayBookingLink(leg)} target="_blank" rel="noopener noreferrer"
              title={`${leg.departure_date} · ${leg.departure_time.slice(0, 5)} – ${leg.arrival_time.slice(0, 5)}`}
              className="flex min-h-[32px] items-center gap-1.5 rounded-full bg-[#F1EBFC] px-3 text-[11px] font-bold text-[#712FFF] hover:bg-[#E7DCFA]">{leg.departure_iata} → {leg.destination_iata}<External size={12}/></a>)}
          </> : flight.booking_link && <a href={flight.booking_link} target="_blank" rel="noopener noreferrer"
            className="flex min-h-[32px] items-center gap-1.5 rounded-full bg-[#F1EBFC] px-3 text-[11px] font-bold text-[#712FFF] hover:bg-[#E7DCFA]">{t("flight.book")}<External size={12}/></a>}
        </div>
      </div>
    </details>
  </div>;
}
